import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { A11yPrefs } from './types'
import { DEFAULT_PREFS, CYCLE_NEXT } from './constants'

interface A11yStore extends A11yPrefs {
  cycleControl: (key: keyof A11yPrefs) => void
  setWidgetEnabled: (enabled: boolean) => void
  resetAll: () => void
  rehydrate: () => void
}

function getOSDefaults(): Partial<A11yPrefs> {
  if (typeof window === 'undefined') return {}
  const overrides: Partial<A11yPrefs> = {}
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    overrides.motion = 'reduced'
  }
  if (window.matchMedia('(prefers-contrast: more)').matches) {
    overrides.contrast = 'on'
  }
  return overrides
}

export const useA11yStore = create<A11yStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_PREFS,

      cycleControl: (key: keyof A11yPrefs) => {
        if (key === 'widgetEnabled') {
          set({ widgetEnabled: !(get().widgetEnabled as boolean) })
          return
        }

        const current = get()[key]
        const cycles = CYCLE_NEXT[key as keyof typeof CYCLE_NEXT]
        if (!cycles || typeof current !== 'string') return

        const nextValue = cycles[current as never]
        set({ [key]: nextValue })
      },

      setWidgetEnabled: (enabled: boolean) => {
        set({ widgetEnabled: enabled })
      },

      resetAll: () => {
        set(DEFAULT_PREFS)
      },

      rehydrate: () => {
        const hasStoredState = typeof localStorage !== 'undefined' &&
          localStorage.getItem('a11y-prefs') !== null
        useA11yStore.persist.rehydrate()
        if (!hasStoredState) {
          const osDefaults = getOSDefaults()
          set(osDefaults)
        }
      },
    }),
    {
      name: 'a11y-prefs',
      skipHydration: true,
    }
  )
)
